import { useState, useEffect } from "react";
import {
  listProductComponents,
  createProductComponent,
  updateProductComponent,
  deleteProductComponent,
  listProducts,
  type ProductComponentOut,
  type Product,
} from "../../../api/catalog";
import { Button } from "../../primitives/Button";
import { Input } from "../../primitives/Input";
import { Modal } from "../../primitives/Modal";

type ProductComponentsTabProps = {
  productId: string;
};

export function ProductComponentsTab({ productId }: ProductComponentsTabProps) {
  const [components, setComponents] = useState<ProductComponentOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState<ProductComponentOut | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [addChildId, setAddChildId] = useState("");
  const [addQty, setAddQty] = useState("1");
  const [editQty, setEditQty] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editPolicy, setEditPolicy] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await listProductComponents(productId);
      setComponents(list);
    } catch (e) {
      console.error("Failed to load components", e);
      setComponents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [productId]);

  const openAdd = async () => {
    try {
      const res = await listProducts({ page_size: 500, product_type: "produit" });
      setProducts(res.items || []);
      setAddChildId("");
      setAddQty("1");
      setAddModalOpen(true);
    } catch (e) {
      alert((e as Error).message || "Erreur chargement produits");
    }
  };

  const handleAdd = async () => {
    const qty = parseFloat(addQty);
    if (!addChildId || isNaN(qty) || qty <= 0) return;
    if (addChildId === productId) {
      alert("Le parent et l'enfant ne peuvent pas être le même produit.");
      return;
    }
    setSubmitting(true);
    try {
      await createProductComponent(productId, {
        child_product_id: addChildId,
        qty,
        consumption_policy: "consume_on_sale",
      });
      setAddModalOpen(false);
      await load();
    } catch (e) {
      alert((e as Error).message || "Erreur lors de l'ajout");
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (comp: ProductComponentOut) => {
    setEditModalOpen(comp);
    setEditQty(String(comp.qty));
    setEditNotes(comp.notes || "");
    setEditPolicy(comp.consumption_policy || "consume_on_sale");
  };

  const handleEdit = async () => {
    if (!editModalOpen) return;
    const qty = parseFloat(editQty);
    if (isNaN(qty) || qty <= 0) return;
    setSubmitting(true);
    try {
      await updateProductComponent(productId, editModalOpen.id, {
        qty,
        notes: editNotes || null,
        consumption_policy: editPolicy || null,
      });
      setEditModalOpen(null);
      await load();
    } catch (e) {
      alert((e as Error).message || "Erreur lors de la mise à jour");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (comp: ProductComponentOut) => {
    if (!confirm(`Retirer le composant « ${comp.child_product_name} » du BOM ?`)) return;
    try {
      await deleteProductComponent(productId, comp.id);
      await load();
    } catch (e) {
      alert((e as Error).message || "Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted">
          Composants (BOM) : pièces ou sous-ensembles qui constituent ce produit.
        </p>
        <Button label="Ajouter un composant" intent="primary" size="sm" onClick={openAdd} />
      </div>
      {components.length === 0 ? (
        <p className="text-sm text-muted">Aucun composant. Ajoutez des pièces pour définir le BOM.</p>
      ) : (
        <div className="rounded border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-muted border-b border-border">
                <th className="text-left p-3 font-medium text-text">Code / Nom</th>
                <th className="text-right p-3 font-medium text-text">Quantité</th>
                <th className="text-left p-3 font-medium text-text">Politique</th>
                <th className="text-right p-3 font-medium text-text">Actions</th>
              </tr>
            </thead>
            <tbody>
              {components.map((comp) => (
                <tr key={comp.id} className="border-b border-border last:border-0">
                  <td className="p-3">
                    <span className="font-medium">{comp.child_product_code || comp.child_product_id}</span>
                    <span className="text-muted ml-2">{comp.child_product_name}</span>
                  </td>
                  <td className="p-3 text-right">{comp.qty}</td>
                  <td className="p-3 text-muted">{comp.consumption_policy || "—"}</td>
                  <td className="p-3 text-right">
                    <button
                      type="button"
                      className="text-primary hover:underline text-xs mr-2"
                      onClick={() => openEdit(comp)}
                    >
                      Modifier
                    </button>
                    <button
                      type="button"
                      className="text-danger hover:underline text-xs"
                      onClick={() => handleDelete(comp)}
                    >
                      Retirer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal title="Ajouter un composant" open={addModalOpen} onClose={() => setAddModalOpen(false)} showDefaultButtons={false} showCloseButton>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text mb-1">Produit (pièce)</label>
            <select
              className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
              value={addChildId}
              onChange={(e) => setAddChildId(e.target.value)}
            >
              <option value="">— Sélectionner —</option>
              {products
                .filter((p) => p.id !== productId)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code || p.id} — {p.name}
                  </option>
                ))}
            </select>
          </div>
          <Input
            label="Quantité par unité"
            type="number"
            min={0.0001}
            step={1}
            value={addQty}
            onChange={(e) => setAddQty(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button label="Annuler" intent="default" size="sm" onClick={() => setAddModalOpen(false)} />
            <Button label="Ajouter" intent="primary" size="sm" onClick={handleAdd} disabled={submitting || !addChildId} />
          </div>
        </div>
      </Modal>

      {editModalOpen && (
        <Modal title="Modifier le composant" open={true} onClose={() => setEditModalOpen(null)} showDefaultButtons={false} showCloseButton>
          <div className="space-y-4">
            <p className="text-sm text-muted">{editModalOpen.child_product_name}</p>
            <Input
              label="Quantité par unité"
              type="number"
              min={0.0001}
              step={1}
              value={editQty}
              onChange={(e) => setEditQty(e.target.value)}
            />
            <Input label="Notes" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Optionnel" />
            <div>
              <label className="block text-sm font-medium text-text mb-1">Politique de consommation</label>
              <select
                className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
                value={editPolicy}
                onChange={(e) => setEditPolicy(e.target.value)}
              >
                <option value="consume_on_sale">Consommer à la vente</option>
                <option value="informational_only">Information uniquement</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button label="Annuler" intent="default" size="sm" onClick={() => setEditModalOpen(null)} />
              <Button label="Enregistrer" intent="primary" size="sm" onClick={handleEdit} disabled={submitting} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
